import React, { useState, useCallback } from 'react';
import { useCRM } from '../../utils/CRMContext';
import { useAuth } from '../../utils/AuthContext';
import { LeadSource, LeadStatus, DEFAULT_LEAD_COORDINATES } from '../../types';
import { geocodeStructuredAddress } from '../../utils/geocoding';
import './QuickAddLeadModal.css';

interface QuickAddLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLeadCreated?: (leadId: string) => void;
}

const LEAD_SOURCES: LeadSource[] = [
  'web-form',
  'facebook-form',
  'instagram-form',
  'referral',
  'cold-call',
  'call-in',
  'b2b-visit',
  'ebook',
  'instagram-follow',
  'facebook-follow',
  'unspecified',
];

const SOURCE_LABELS: Record<LeadSource, string> = {
  'web-form': 'Web Form',
  'facebook-form': 'Facebook Form',
  'instagram-form': 'Instagram Form',
  'referral': 'Referral',
  'cold-call': 'Cold Call',
  'call-in': 'Call-in',
  'b2b-visit': 'B2B Visit',
  'ebook': 'eBook',
  'instagram-follow': 'Instagram Follow',
  'facebook-follow': 'Facebook Follow',
  'unspecified': 'Unspecified',
};

const QuickAddLeadModal: React.FC<QuickAddLeadModalProps> = ({
  isOpen,
  onClose,
  onLeadCreated,
}) => {
  const { createLead } = useCRM();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [location, setLocation] = useState('');
  const [source, setSource] = useState<LeadSource>('web-form');
  const [notes, setNotes] = useState('');

  const resetForm = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setLocation('');
    setSource('web-form');
    setNotes('');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Basic validation
    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Geocode the location if provided
      let coordinates = DEFAULT_LEAD_COORDINATES;
      let geocoded = false;

      if (location.trim()) {
        try {
          const geocodedCoords = await geocodeStructuredAddress(location);
          if (geocodedCoords) {
            coordinates = geocodedCoords;
            geocoded = true;
          }
        } catch (geocodeError) {
          console.warn('Geocoding failed:', geocodeError);
        }
      }

      const leadId = await createLead({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        location: location.trim(),
        source,
        status: 'new' as LeadStatus,
        notes: notes.trim() || undefined,
        projectType: 'Custom Timber Frame',
        budget: 0,
        timeline: 'Not specified',
        coordinates,
        geocoded,
        archived: false,
        labels: [],
        addedBy: user?.id, // Track who added the lead
      });

      resetForm();
      onClose();
      
      if (onLeadCreated) {
        onLeadCreated(leadId);
      }
    } catch (err) {
      console.error('Error creating lead:', err);
      setError(err instanceof Error ? err.message : 'Failed to create lead');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="quick-add-lead-overlay" onClick={handleClose}>
      <div className="quick-add-lead-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Lead</h2>
          <button className="close-btn" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="quick-add-form">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-row two-columns">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
              />
            </div>
          </div>

          <div className="form-row two-columns">
            <div className="form-group">
              <label htmlFor="email">Email *</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone</label>
              <input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="form-row two-columns">
            <div className="form-group">
              <label htmlFor="location">Location</label>
              <input
                type="text"
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Nashville, TN"
              />
            </div>
            <div className="form-group">
              <label htmlFor="source">Source</label>
              <select
                id="source"
                value={source}
                onChange={(e) => setSource(e.target.value as LeadSource)}
              >
                {LEAD_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional details..."
              rows={3}
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Lead'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickAddLeadModal;
